import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {SaleOfferDto, SaleOfferItemDto} from '../../../../models/generated-models';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {ToastService} from '../../../shared/commons/toast/toast-service';
import {RecordCodeComponent} from '../../../shared/commons/trade/record-code.component';
import {downloadQrImage, renderQrDataUrl} from '../../../shared/commons/trade/qr-code.utils';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {
  DEFAULT_TRADE_UNIT,
  TRADE_BASE,
  saleOfferStatusBadgeClass,
  saleOfferStatusLabel
} from '../trade.constants';
import {formatQuantityWithUnit, offerComponentsBreakdown} from '../trade-utils';

/**
 * A sale offer (ใบขาย) the seller shows to a buyer: the QR carries the offer
 * code, and the buyer accepts at most the quantity written here — a line the
 * buyer took only part of shows what was taken next to what was offered, and
 * the remainder went back to this seller's stock. While the offer is open the
 * QR is rendered right on the page, so the seller can hold the phone up without
 * another tap.
 */
@Component({
  selector: 'app-trade-offer',
  standalone: true,
  imports: [RouterLink, PageTitleComponent, ThaiDateTimePipe, RecordCodeComponent],
  templateUrl: './trade-offer.component.html',
  styleUrl: './trade-offer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeOfferComponent implements OnInit {
  private readonly api = inject(TradeApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly store = inject(TradeStore);

  protected readonly offerId = signal<number | null>(null);
  protected readonly offer = signal<SaleOfferDto | null>(null);
  protected readonly loading = signal(false);
  protected readonly notFound = signal(false);
  protected readonly cancelling = signal(false);
  /** PNG data URL of the offer-code QR; null until rendered or when the code is missing. */
  protected readonly qrImageUrl = signal<string | null>(null);
  /** True for a moment after the code under the QR was copied, so the button can say so. */
  protected readonly codeCopied = signal(false);
  private codeCopiedTimeout: ReturnType<typeof setTimeout> | null = null;

  protected readonly unit = DEFAULT_TRADE_UNIT;
  protected readonly tradeBase = TRADE_BASE;

  protected readonly saleOfferStatusLabel = saleOfferStatusLabel;
  protected readonly saleOfferStatusBadgeClass = saleOfferStatusBadgeClass;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;
  protected readonly offerComponentsBreakdown = offerComponentsBreakdown;

  /** One line per product sold under this offer. */
  protected readonly items = computed<SaleOfferItemDto[]>(() => this.offer()?.items ?? []);

  /** Names of distinct products in the offer, e.g. "ทุเรียนหมอนทอง" or joined if multiple. */
  protected readonly productNames = computed<string>(() => {
    const list = this.items()
      .map(i => i.productName?.trim())
      .filter((name): name is string => Boolean(name));
    const unique = Array.from(new Set(list));
    return unique.join(', ');
  });

  protected readonly totalQuantity = computed(
    () => this.offer()?.totalQuantity
      ?? this.items().reduce((sum, item) => sum + (item.quantity ?? 0), 0)
  );

  protected readonly isOpen = computed(() => this.offer()?.status === 'OPEN');

  protected readonly isAccepted = computed(() => this.offer()?.status === 'ACCEPTED');

  /** The viewer is on the selling side (this is also what lets them cancel). */
  protected readonly isSeller = computed(() => this.offer()?.owned === true);

  /**
   * The viewer's own business is the one that accepted, so the page reads as their
   * purchase ("รับซื้อจาก …") rather than as somebody else's ใบขาย. Staff who are on
   * neither side keep the seller-worded view.
   */
  protected readonly isBuyer = computed(() => {
    const offer = this.offer();
    if (!offer || offer.owned || offer.status !== 'ACCEPTED' || offer.acceptedBusinessId == null) {
      return false;
    }
    return this.store.businesses().some((business) => business.id === offer.acceptedBusinessId);
  });

  protected readonly pageTitle = computed(() => (this.isBuyer() ? 'รับซื้อจากใบขาย' : 'ใบขาย'));

  /** The single purchase record behind an accepted offer, when there is exactly one. */
  protected readonly soleAcceptedRecordId = computed<number | null>(() => {
    const ids = this.offer()?.acceptedRecordIds ?? [];
    return ids.length === 1 ? ids[0] : null;
  });

  /** What the buyer actually took, line by line (the full line until it is accepted). */
  protected readonly acceptedTotalQuantity = computed(
    () => this.items().reduce((sum, item) => sum + (item.acceptedQuantity ?? item.quantity ?? 0), 0)
  );

  /** True when the buyer took less than the offer on at least one line. */
  protected readonly partiallyAccepted = computed(
    () => this.isAccepted() && this.items().some((item) => this.takenLess(item))
  );

  /** The buyer's quantity is only worth showing when it differs from the offered one. */
  protected takenLess(item: SaleOfferItemDto): boolean {
    return this.isAccepted() && item.acceptedQuantity != null && item.acceptedQuantity !== item.quantity;
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isInteger(id)) {
      this.notFound.set(true);
      return;
    }
    this.offerId.set(id);
    if (!this.store.businessesLoaded()) {
      // Needed to tell whether the accepting business is one of the viewer's own.
      void this.store.loadBusinesses();
    }
    this.load(id);
  }

  protected downloadQr(): void {
    downloadQrImage(this.qrImageUrl(), this.offer()?.offerCode ?? 'qr-code');
  }

  /** Copies the offer code shown under the QR (work item 178). */
  protected copyOfferCode(): void {
    const code = this.offer()?.offerCode?.trim();
    if (!code || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(code).then(() => {
      this.toast.success('คัดลอกรหัสใบขายแล้ว');
      this.codeCopied.set(true);
      if (this.codeCopiedTimeout) {
        clearTimeout(this.codeCopiedTimeout);
      }
      this.codeCopiedTimeout = setTimeout(() => this.codeCopied.set(false), 2000);
    });
  }

  protected printReceipt(): void {
    window.print();
  }

  protected cancelOffer(): void {
    const id = this.offerId();
    if (id == null || this.cancelling()) {
      return;
    }
    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการยกเลิก',
      content: 'ต้องการยกเลิกใบขายนี้หรือไม่ ระบบจะคืนสินค้าเข้าสต็อก',
      confirmButtonMsg: 'ยกเลิกใบขาย',
      closeButtonMsg: 'ปิด'
    })).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.cancelling.set(true);
      this.api.cancelOffer(id).subscribe({
        next: (updated) => {
          this.offer.set(updated);
          this.cancelling.set(false);
        },
        error: () => this.cancelling.set(false)
      });
    }).catch(() => undefined);
  }

  private load(id: number): void {
    this.loading.set(true);
    this.notFound.set(false);
    this.api.getOffer(id).subscribe({
      next: (offer) => {
        this.offer.set(offer);
        this.notFound.set(!offer);
        this.loading.set(false);
        void this.renderQr(offer?.offerCode);
      },
      error: () => {
        this.offer.set(null);
        this.notFound.set(true);
        this.loading.set(false);
      }
    });
  }

  /** Rendered large so it stays crisp on a phone held up to a scanner. */
  private async renderQr(code: string | null | undefined): Promise<void> {
    this.qrImageUrl.set(await renderQrDataUrl(code, 512));
  }
}
